import { Stage } from "@pixi/react"
import { t } from "i18next"
import { TextMetrics } from "pixi.js"
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { nodeTextStyle, PADDING_Y } from "@/domains/visualization/constants/graph"
import { useGetRelatedCustomers } from "@/domains/visualization/hooks/useGetRelatedCustomers"
import { selectRootCustomerId } from "@/domains/visualization/store/selectors.ts"
import { useGraphStore } from "@/domains/visualization/store/store.ts"
import { Loading } from "@/shared/components/Loading/Loading.tsx"
import { StatusAlert } from "@/shared/components/StatusAlert/StatusAlert"
import { throttle } from "@/shared/utils/throttle/throttle"
import { GraphPreviewCanvas } from "../GraphPreviewCanvas/GraphPreviewCanvas.tsx"

const HEIGHT = 275

export function GraphPreview() {
  const rootCustomerId = useGraphStore(selectRootCustomerId)
  const query = useGetRelatedCustomers(rootCustomerId)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [stageDimensions, setStageDimensions] = useState({
    width: 0,
    height: 0
  })

  const relatedCustomers = query.data?.data.relatedCustomers ?? []
  const customerEdges = query.data?.data.customerEdges ?? []

  const updateDimensions = useCallback(() => {
    if (wrapperRef.current) {
      const { clientWidth, clientHeight } = wrapperRef.current
      if (clientWidth > 0 && clientHeight > 0) {
        setStageDimensions({
          width: clientWidth,
          height: clientHeight
        })
      }
    }
  }, [])

  const throttledUpdateDimensions = useMemo(() => {
    return throttle(() => {
      updateDimensions()
    }, 500)
  }, [updateDimensions])

  // Set up ResizeObserver and get initial dimensions
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      throttledUpdateDimensions()
    })

    let isSetupComplete = false
    let timeoutId: number | null = null

    // Initial dimension update with retry logic
    const checkAndUpdateDimensions = () => {
      if (isSetupComplete) return

      if (wrapperRef.current) {
        const { clientWidth, clientHeight } = wrapperRef.current
        if (clientWidth > 0 && clientHeight > 0) {
          isSetupComplete = true
          updateDimensions()
          observer.observe(wrapperRef.current)
        } else {
          // Retry if no dimensions yet
          timeoutId = window.setTimeout(checkAndUpdateDimensions, 10)
        }
      } else {
        // Retry if ref not ready
        timeoutId = window.setTimeout(checkAndUpdateDimensions, 10)
      }
    }

    checkAndUpdateDimensions()

    return () => {
      isSetupComplete = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      observer.disconnect()
      throttledUpdateDimensions.cancel()
    }
  }, [throttledUpdateDimensions, updateDimensions])

  if (query.isLoading) {
    return <Loading text={t("customer.overview.loadingHierarchy")} />
  }

  if (query.isError || !query.data) {
    return (
      <StatusAlert
        color="error"
        message={query.error?.message ?? t("errorBoundary.defaultDescription")}
        retry={() => void query.refetch()}
        retryLabel={t("errorBoundary.button")}
      />
    )
  }

  const isSingleRootCustomer = relatedCustomers.length === 1 && relatedCustomers[0].id === rootCustomerId
  const metrics = TextMetrics.measureText(relatedCustomers[0].name, nodeTextStyle)
  const height = metrics.height + PADDING_Y * 4
  const containerHeight = isSingleRootCustomer ? height : HEIGHT

  const containerStyle: CSSProperties = {
    position: "relative",
    height: containerHeight,
    width: "100%",
    overflow: "hidden"
  }

  const stageStyle: CSSProperties = {
    width: "100%",
    height: containerHeight,
    position: "absolute",
    top: 0,
    left: 0
  }

  const cappedResolution = Math.min(window.devicePixelRatio || 1, 2)

  return (
    <div ref={wrapperRef} style={containerStyle}>
      <Stage
        style={stageStyle}
        width={stageDimensions.width}
        height={stageDimensions.height}
        options={{
          backgroundColor: 0xffffff,
          antialias: false,
          resolution: cappedResolution,
          autoDensity: true,
          powerPreference: "high-performance"
        }}
      >
        <GraphPreviewCanvas
          nodes={relatedCustomers}
          edges={customerEdges}
          wrapperRef={wrapperRef}
          canvasWidth={stageDimensions.width}
          canvasHeight={stageDimensions.height}
          disablePanAndZoom={isSingleRootCustomer}
        />
      </Stage>
    </div>
  )
}
